import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { AppLayout } from "@/components/layout/app-layout";
import { AuthLayout } from "@/components/layout/auth-layout";
import { ErrorPage } from "@/pages/error-page";

// Lazy-loaded pages
const DashboardPage = lazy(() =>
	import("@/pages/dashboard/dashboard-page").then((m) => ({
		default: m.DashboardPage,
	})),
);

const AssetsPage = lazy(() =>
	import("@/pages/assets/assets-page").then((m) => ({
		default: m.AssetsPage,
	})),
);

const AssetDetailPage = lazy(() =>
	import("@/pages/assets/asset-detail-page").then((m) => ({
		default: m.AssetDetailPage,
	})),
);

const AssetFormPage = lazy(() =>
	import("@/pages/assets/asset-form-page").then((m) => ({
		default: m.AssetFormPage,
	})),
);

const AssetLifeSheetPage = lazy(() =>
	import("@/pages/assets/asset-life-sheet-page").then((m) => ({
		default: m.AssetLifeSheetPage,
	})),
);

const AssetTypesPage = lazy(() =>
	import("@/pages/asset-types/asset-types-page").then((m) => ({
		default: m.AssetTypesPage,
	})),
);

const AssetTypeFormPage = lazy(() =>
	import("@/pages/asset-types/asset-type-form-page").then((m) => ({
		default: m.AssetTypeFormPage,
	})),
);

const LocationsPage = lazy(() =>
	import("@/pages/locations/locations-page").then((m) => ({
		default: m.LocationsPage,
	})),
);

const LocationFormPage = lazy(() =>
	import("@/pages/locations/location-form-page").then((m) => ({
		default: m.LocationFormPage,
	})),
);

const UsersPage = lazy(() =>
	import("@/pages/users/users-page").then((m) => ({
		default: m.UsersPage,
	})),
);

const SettingsPage = lazy(() =>
	import("@/pages/settings/settings-page").then((m) => ({
		default: m.SettingsPage,
	})),
);

const LoginPage = lazy(() =>
	import("@/pages/auth/login-page").then((m) => ({
		default: m.LoginPage,
	})),
);

const ForgotPasswordPage = lazy(() =>
	import("@/pages/auth/forgot-password-page").then((m) => ({
		default: m.ForgotPasswordPage,
	})),
);

const VerifyTotpPage = lazy(() =>
	import("@/pages/auth/verify-totp-page").then((m) => ({
		default: m.VerifyTotpPage,
	})),
);

const NotFoundPage = lazy(() =>
	import("@/pages/not-found-page").then((m) => ({
		default: m.NotFoundPage,
	})),
);

export const router = createBrowserRouter([
	// Public routes
	{
		element: <AuthLayout />,
		errorElement: <ErrorPage />,
		children: [
			{
				path: "/login",
				element: <LoginPage />,
			},
			{
				path: "/verify-totp",
				element: <VerifyTotpPage />,
			},
			{
				path: "/forgot-password",
				element: <ForgotPasswordPage />,
			},
		],
	},

	// Protected routes
	{
		element: (
			<ProtectedRoute>
				<AppLayout />
			</ProtectedRoute>
		),
		errorElement: <ErrorPage />,
		children: [
			{
				path: "/",
				element: <Navigate to="/dashboard" replace />,
			},
			{
				path: "/dashboard",
				element: <DashboardPage />,
				handle: {
					crumb: "Dashboard",
				},
			},
			{
				path: "/assets",
				handle: {
					crumb: "Activos",
				},
				children: [
					{
						index: true,
						element: <AssetsPage />,
					},
					{
						path: "new",
						element: <AssetFormPage />,
						handle: {
							crumb: "Nuevo",
						},
					},
					{
						path: ":id",
						element: <AssetDetailPage />,
						handle: {
							crumb: "Detalle",
						},
					},
					{
						path: ":id/edit",
						element: <AssetFormPage />,
						handle: {
							crumb: "Editar",
						},
					},
				],
			},
			{
				path: "/asset-types",
				handle: {
					crumb: "Tipos de Activo",
				},
				children: [
					{
						index: true,
						element: <AssetTypesPage />,
					},
					{
						path: "new",
						element: <AssetTypeFormPage />,
						handle: {
							crumb: "Nuevo",
						},
					},
					{
						path: ":id/edit",
						element: <AssetTypeFormPage />,
						handle: {
							crumb: "Editar",
						},
					},
				],
			},
			{
				path: "/locations",
				handle: {
					crumb: "Ubicaciones",
				},
				children: [
					{
						index: true,
						element: <LocationsPage />,
					},
					{
						path: "new",
						element: <LocationFormPage />,
						handle: {
							crumb: "Nueva",
						},
					},
					{
						path: ":id/edit",
						element: <LocationFormPage />,
						handle: {
							crumb: "Editar",
						},
					},
				],
			},
			{
				path: "/users",
				element: <UsersPage />,
				handle: {
					crumb: "Usuarios",
				},
			},
			{
				path: "/settings",
				element: <SettingsPage />,
				handle: {
					crumb: "Configuracion",
				},
			},
		],
	},

	// Protected routes without AppLayout (for print-friendly pages)
	{
		element: <ProtectedRoute />,
		children: [
			{
				path: "/assets/:id/life-sheet",
				element: <AssetLifeSheetPage />,
			},
		],
	},

	// 404
	{
		path: "*",
		element: <NotFoundPage />,
	},
]);
